import { useEffect, useRef, useState } from 'react';
import { MessageCircleQuestion } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Sporsmal, Tolkning } from '../../api/reise';

interface Props {
  sporsmal: Sporsmal;
  tolkning: Tolkning;
  lagrer: boolean;
  feil: string | null;
  /** Forslaget stemmer. */
  onBekreft: () => void;
  /** Kunden har svart på oppfølgingsspørsmålene. Svaret tolkes på nytt. */
  onOppfolging: (svar: { sporsmal: string; svar: string }[]) => void;
  /** Tilbake til spørsmålet, for å endre svaret. */
  onEndre: () => void;
  /** Etter et avvik: videre til neste spørsmål. */
  onFortsett: () => void;
}

/**
 * Intervjuassistentens forslag etter «Annet»: forståelsen i én setning, forslaget (merket som tolket), og 1–2
 * oppfølgingsspørsmål. Kunden bekrefter, svarer på oppfølgingen, eller endrer svaret. Ved avvik er svaret lagret
 * slik kunden skrev det.
 */
export default function TolkningPanel({ sporsmal, tolkning, lagrer, feil, onBekreft, onOppfolging, onEndre, onFortsett }: Props) {
  const { t } = useTranslation();
  const overskrift = useRef<HTMLHeadingElement>(null);
  const [valg, setValg] = useState<Record<number, string>>({});

  useEffect(() => {
    overskrift.current?.focus();
  }, [tolkning]);

  const svar = tolkning.valgte.map((v) => (v.verdi === 'annet' ? t('reise.oversikt.annet', { tekst: tolkning.kundensOrd ?? '' }) : v.tekst)).join(', ');
  const besvart = tolkning.oppfolging.map((o, i) => ({ sporsmal: o.tekst, svar: valg[i] })).filter((o): o is { sporsmal: string; svar: string } => !!o.svar);

  return (
    <section aria-labelledby="tolkning-tittel" className="kort p-6 sm:p-8">
      <p className="mb-1 text-sm text-slate-600">{sporsmal.tekst}</p>
      <h2 id="tolkning-tittel" ref={overskrift} tabIndex={-1} className="mb-3 flex items-center gap-2 text-xl font-semibold">
        <MessageCircleQuestion aria-hidden="true" className="h-5 w-5 shrink-0" />
        {t('reise.tolkning.tittel')}
      </h2>

      {tolkning.status === 'avvik' ? (
        <>
          <p role="status" className="mb-4 melding-advarsel">
            {t('reise.tolkning.avvik')}
          </p>
          <button type="button" onClick={onFortsett} className="knapp-primaer">
            {t('reise.tolkning.fortsett')}
          </button>
        </>
      ) : (
        <>
          {tolkning.gjentakelse && (
            <div className="mb-3">
              <p className="text-sm font-medium text-slate-700">{t('reise.tolkning.forstaelse')}</p>
              <p className="text-lg italic text-slate-900">{tolkning.gjentakelse}</p>
            </div>
          )}
          <p className="mb-1.5 text-sm font-medium text-slate-700">{t('reise.tolkning.forslag', { svar })}</p>
          <p className="mb-4 text-sm text-slate-600">{t('reise.tolkning.antatt')}</p>

          {tolkning.oppfolging.length > 0 && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="mb-3 font-medium">{t('reise.tolkning.oppfolging')}</p>
              {tolkning.oppfolging.map((o, i) => (
                <fieldset key={i} className="mb-3">
                  <legend className="mb-2">{o.tekst}</legend>
                  <div className="flex flex-col gap-2">
                    {o.alternativer.map((a) => (
                      <label key={a} className={'valg ' + (valg[i] === a ? 'valg-valgt' : '')}>
                        <input
                          type="radio"
                          name={`oppfolging-${i}`}
                          value={a}
                          checked={valg[i] === a}
                          onChange={() => setValg({ ...valg, [i]: a })}
                          className="h-5 w-5 shrink-0"
                        />
                        <span>{a}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          )}

          {feil && (
            <p role="alert" className="mb-3 melding-feil">
              {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
            </p>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {besvart.length > 0 ? (
              <button type="button" disabled={lagrer} onClick={() => onOppfolging(besvart)} className="knapp-primaer">
                {lagrer ? t('reise.tolkning.tolker') : t('reise.tolkning.sendOppfolging')}
              </button>
            ) : (
              <button type="button" disabled={lagrer} onClick={onBekreft} className="knapp-primaer">
                {t('reise.tolkning.ja')}
              </button>
            )}
            {besvart.length > 0 && (
              <button type="button" disabled={lagrer} onClick={onBekreft} className="knapp-sekundaer">
                {t('reise.tolkning.ja')}
              </button>
            )}
            <button type="button" disabled={lagrer} onClick={onEndre} className="knapp-sekundaer">
              {t('reise.tolkning.endre')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
